import { keepPreviousData, queryOptions } from "@tanstack/react-query"

import { getIsomorphicSupabaseClient } from "@repo/supabase/isomorphic-client"
import type { DataTableBaseSearch } from "~/lib/schemas/common"
import { createBusinessEntitiesService } from "~/modules/business-entities/business-entities.service"

export const getExtendedBusinessEntitiesQueryOptions = ({
  filters,
}: {
  filters?: DataTableBaseSearch
} = {}) =>
  queryOptions({
    queryKey: filters
      ? ["extended-business-entities", filters]
      : ["extended-business-entities"],
    queryFn: async () => {
      const supabase = await getIsomorphicSupabaseClient()
      const businessEntitiesService = createBusinessEntitiesService(supabase)
      return businessEntitiesService.getManyExtended(filters)
    },
    staleTime: 1000 * 60 * 30, // 30 minutes
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    placeholderData: keepPreviousData,
  })

export const getBusinessEntityInvoicePeriodsQueryOptions = ({
  businessEntityId,
}: {
  businessEntityId: string
}) =>
  queryOptions({
    queryKey: ["business-entity-invoice-periods", businessEntityId],
    queryFn: async () => {
      const supabase = await getIsomorphicSupabaseClient()
      const businessEntitiesService = createBusinessEntitiesService(supabase)
      return businessEntitiesService.getInvoicePeriods({ businessEntityId })
    },
    staleTime: 1000 * 60 * 30, // 30 minutes
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  })

export const getBusinessEntityCurrenciesQueryOptions = ({
  businessEntityId,
}: {
  businessEntityId: string
}) =>
  queryOptions({
    queryKey: ["business-entity-currencies", businessEntityId],
    queryFn: async () => {
      const supabase = await getIsomorphicSupabaseClient()
      const businessEntitiesService = createBusinessEntitiesService(supabase)
      return businessEntitiesService.getCurrencies({
        businessEntityId,
      })
    },
  })

export const getExtendedBusinessEntityQueryOptions = ({
  businessEntitySlug,
}: {
  businessEntitySlug: string
}) =>
  queryOptions({
    queryKey: ["extended-business-entity", businessEntitySlug],
    queryFn: async () => {
      const supabase = await getIsomorphicSupabaseClient()
      const businessEntitiesService = createBusinessEntitiesService(supabase)
      return businessEntitiesService.getFromSlug({ slug: businessEntitySlug })
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  })

export const getBusinessEntityOverviewStatsQueryOptions = ({
  businessEntitySlug,
}: {
  businessEntitySlug: string
}) =>
  queryOptions({
    queryKey: ["business-entity-overview-stats", businessEntitySlug],
    queryFn: async () => {
      const supabase = await getIsomorphicSupabaseClient()
      const businessEntitiesService = createBusinessEntitiesService(supabase)
      return businessEntitiesService.getOverviewStats(businessEntitySlug)
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  })
